import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Component, OnInit, ViewChild, inject, ElementRef, HostListener, viewChild, DestroyRef } from "@angular/core";
import { PopoverDirective } from "ngx-bootstrap/popover";
import {
    CardViewService,
    DashboardsService,
    FiltersService,
    LocalStorageService,
    FilterSortModalHandlerService,
    HelpersService,
    AnalyticsService,
} from "../shared";
import { IDashboardModel, isJobsBillingDashboard, isiFirmTaxDashboard } from "../views-interfaces/dashboard-model.interface";
import { SortGeneratorService } from "../filter-menu/sort-generator.service";
import { SingleSelectModel, Option } from "../filter-menu/filter-types";

import { JumpstartComponentsWkIconsModule, JumpstartComponentsModule } from "@wk/components-v3-angular17";
import { CdkTrapFocus } from "@angular/cdk/a11y";
import { KendoDropDownListWrapper } from "../shared/components/kendo-dropdownlist-wrapper/kendo-dropdownlist-wrapper.component";
import { FormsModule } from "@angular/forms";

@Component({
    selector: "wk-sort-menu",
    templateUrl: "./sort-menu.component.html",
    styleUrl: "./sort-menu.component.scss",
    host: { class: "wk-sortmenu" },
    imports: [PopoverDirective, JumpstartComponentsWkIconsModule, JumpstartComponentsModule, CdkTrapFocus, KendoDropDownListWrapper, FormsModule],
})
export class SortMenuComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly cardViewService = inject(CardViewService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly modalCoordinationService = inject(FilterSortModalHandlerService);
    private readonly sortGeneratorService = inject(SortGeneratorService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly filtersService = inject(FiltersService);
    private readonly helpersService = inject(HelpersService);
    private currentDashboard: IDashboardModel | undefined;
    @ViewChild(PopoverDirective, { static: false }) popoverDirective: PopoverDirective;
    @ViewChild("sortPopup") sortPopupButton!: ElementRef<HTMLButtonElement>;
    private readonly sortPopover = viewChild<ElementRef<HTMLElement>>("sortPopover");
    private static readonly SORT_ANALYTICS_EVENT_NAME = "Change sort by";

    sortByModel: SingleSelectModel;
    sortOrderModel: SingleSelectModel;
    isiFirmTaxDashboard = false;
    isJobsBillingDashboard = false;
    isPopoverOpen = false;
    hasPendingFavoriteFilterChanges = false;

    constructor() {}

    ngOnInit(): void {
        // Listen for modal coordination to close popover when filter menu opens
        this.modalCoordinationService.activeModal$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((activeModal) => {
            if (activeModal !== "sort" && this.popoverDirective?.isOpen) {
                this.popoverDirective.hide();
            }
            // Open popover when activeModal is set to "sort" and popover is not already open
            if (activeModal === "sort" && this.popoverDirective && !this.popoverDirective.isOpen) {
                setTimeout(() => {
                    this.popoverDirective.show();
                }, 50);
            }
        });

        this.helpersService.favouriteFiltersPendingChangesEvent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
            this.hasPendingFavoriteFilterChanges = event;
        });

        this.dashboardsService.currentDashboardChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (currentDashboardId) => {
            const currentDashboard = await this.dashboardsService.getCurrentDashboard();
            if (!currentDashboard) {
                return;
            }
            this.currentDashboard = currentDashboard;
            this.isiFirmTaxDashboard = isiFirmTaxDashboard(this.currentDashboard);
            this.isJobsBillingDashboard = isJobsBillingDashboard(this.currentDashboard);
            const item = localStorage.getItem(`Filter_Selection_${currentDashboardId}`);
            const storedSelection = item && item !== "" ? JSON.parse(item) : {};
            this.loadSort(storedSelection);
        });

        this.cardViewService.viewTypeChangeEvent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (viewType) => {
            if (this.sortByModel) {
                if (viewType != "all") {
                    const item = localStorage.getItem(`Filter_Selection_${this.currentDashboard.Id}`);
                    const storedSelection = item && item !== "" ? JSON.parse(item) : {};

                    const storedSortByForViewType = storedSelection.SortBy?.[viewType] || storedSelection.SortBy;
                    this.sortByModel = this.getSortByModel(storedSortByForViewType, viewType);
                    this.sortOrderModel = this.sortGeneratorService.generateSortOrderModel(storedSelection.SortOrder);

                    this.onSortChanged(this.sortByModel.selection, this.sortOrderModel.selection, !storedSortByForViewType);
                } else {
                    this.sortByModel = this.getSortByModel(null, viewType);
                    this.sortOrderModel = this.sortGeneratorService.generateSortOrderModel(null);

                    this.onSortChanged(this.sortByModel.selection, this.sortOrderModel.selection, false);
                }
            }
        });

        (async () => {
            const currentDashboard = await this.dashboardsService.getCurrentDashboard();
            if (!currentDashboard) {
                return;
            }
            this.currentDashboard = currentDashboard;
            this.isiFirmTaxDashboard = isiFirmTaxDashboard(this.currentDashboard);
            this.isJobsBillingDashboard = isJobsBillingDashboard(this.currentDashboard);
            const item = localStorage.getItem(`Filter_Selection_${this.currentDashboard.Id}`);
            const storedSelection = item && item !== "" ? JSON.parse(item) : {};
            await this.loadSort(storedSelection);
        })();
    }

    closePopover() {
        if (this.popoverDirective?.isOpen) {
            this.popoverDirective.hide();
        }
    }

    async loadSort(storedSelection: any) {
        const viewType = this.cardViewService.currentViewType ?? "all";
        const sortByForViewType = storedSelection.SortBy?.[viewType] || storedSelection.SortBy;
        this.sortByModel = this.getSortByModel(sortByForViewType, viewType);
        this.sortOrderModel = this.sortGeneratorService.generateSortOrderModel(storedSelection.SortOrder);

        this.setSort(this.sortByModel.selection, this.sortOrderModel.selection);
        this.filtersService.handleFilterChange();
    }

    async onSortChanged(sortBySelection: Option, sortOrderSelection: Option, isUpdateRequired: boolean, trackAnalytics = false) {
        if (sortBySelection) {
            this.sortByModel.selection = sortBySelection;
        }
        if (sortOrderSelection) {
            this.sortOrderModel.selection = sortOrderSelection;
        }

        // Track analytics only when manual user actions
        if (trackAnalytics) {
            this.trackSortAnalytics(sortBySelection, sortOrderSelection);
        }

        if (this.cardViewService.currentViewType != "all") {
            if (isUpdateRequired) {
                const viewTypes = ["active", "notStarted", "completed"];

                for (const viewType of viewTypes) {
                    const sortModelForView = this.getSortByModel(null, viewType);
                    const isOptionAvailable = sortModelForView.applicableOptions.some((option) => option.value === sortBySelection?.value);

                    if (isOptionAvailable) {
                        LocalStorageService.setObject(`Filter_Selection_${this.currentDashboard!.Id}`, ["SortBy", viewType], sortBySelection);
                    }
                }
            }
            LocalStorageService.setObject(`Filter_Selection_${this.currentDashboard!.Id}`, ["SortOrder"], sortOrderSelection);
        }

        await this.setSort(sortBySelection, sortOrderSelection);
        this.filtersService.handleFilterChange();
    }

    async setSort(sortBySelection: Option, sortOrderSelection: Option) {
        const sortByValue = typeof sortBySelection?.value === "string" ? sortBySelection.value : null;
        const sortOrderValue = typeof sortOrderSelection?.value === "string" ? sortOrderSelection.value : null;
        this.filtersService.activeSortForPrint = sortByValue ?? undefined;
        const generatedSort = this.isJobsBillingDashboard
            ? await this.sortGeneratorService.generateJobsBillingSortBy(sortByValue, sortOrderValue)
            : await this.sortGeneratorService.generateSortBy(sortByValue, sortOrderValue);
        this.filtersService.activeSort = generatedSort ?? (() => 0);
    }

    onSortButtonClick() {
        if (this.hasPendingFavoriteFilterChanges) {
            this.modalCoordinationService.setActiveModal("pendingChanges");
        } else {
            this.modalCoordinationService.setActiveModal("sort");
        }
    }

    onPopoverShown() {
        requestAnimationFrame(() => {
            this.sortPopover()?.nativeElement?.focus?.({ preventScroll: true });
        });
    }

    getSortByOption(value: string): Option | null {
        return this.sortByModel?.applicableOptions.find((option) => option.value === value) || null;
    }

    getSortOrderOption(value: string): Option | null {
        return this.sortOrderModel?.applicableOptions.find((option) => option.value === value) || null;
    }

    private trackSortAnalytics(sortBySelection: Option, sortOrderSelection: Option): void {
        // Only track if there's actually a sort selection to track
        if (sortBySelection?.value && sortOrderSelection?.value) {
            const analyticsData: Record<string, unknown> = {};

            analyticsData.orderBy = sortBySelection.value;
            analyticsData.orderDirection = sortOrderSelection.value;

            this.analyticsService.track(SortMenuComponent.SORT_ANALYTICS_EVENT_NAME, analyticsData);
        }
    }

    private getSortByModel(selection: Option | null, viewType: string | null): SingleSelectModel {
        return this.isJobsBillingDashboard
            ? this.sortGeneratorService.generateJobsBillingSortByModel(selection, viewType)
            : this.sortGeneratorService.generateSortByModel(selection, viewType, this.isiFirmTaxDashboard, this.currentDashboard!.TaxYear);
    }

    @HostListener("document:keydown.escape", ["$event"]) onEscapeKey(event: Event) {
        const targetElement = event.target as HTMLElement;

        if (targetElement && targetElement.nodeName === "KENDO-DROPDOWNLIST") {
            setTimeout(() => {
                this.sortPopupButton.nativeElement.focus();
            }, 0);
        } else {
            this.closePopover();
        }
    }
}
